import { useState } from "react";
import { Link } from "react-router-dom";
import type { Product } from "../../../types/product";
import { API_CONFIG } from "../../../services/config";
import { useCart } from "../../../context/CartContext";
import { useCompare } from "../../../context/CompareContext";
import { useLang } from "../../../context/LanguageContext";
import SafeImage from "../../../components/base/SafeImage";
import AvailabilityModal from "./AvailabilityModal";

interface Props {
	product: Product;
}

const LOGO_FALLBACK =
	"https://storage.readdy-site.link/project_files/e0ad0187-0f87-44ac-bc81-3163fcc0baf7/856a6a4e-c760-477d-a837-65755837c797_fb6affb0-c65b-4aac-81c4-bfd5377e0e55.webp?v=328770f95ae230a12f1326c5fd47e1ad";

export default function ProductCard({ product }: Props) {
	const { addItem } = useCart();
	const {
		addToCompare,
		removeFromCompare,
		isInCompare,
		compareItems,
		compareModeActive,
	} = useCompare();
	const { t } = useLang();
	const [showAvailability, setShowAvailability] = useState(false);

	const totalStock = product.availability.reduce(
		(sum, a) => sum + a.quantity,
		0,
	);
	const outOfStock = totalStock === 0;

	const inCompare = isInCompare(product.id);
	const canCompare = !inCompare && compareItems.length < 3;

	const catLabel = product.productSubCategory ?? product.category ?? "";

	const stockBadge =
		totalStock > 0
			? { text: "En stock", color: "emerald" }
			: { text: "Sin stock", color: "red" };

	const handleCardClick = (e: React.MouseEvent) => {
		if (compareModeActive && canCompare) {
			e.preventDefault();
			addToCompare(product);
		}
	};

	const imageSrc = (product.images?.[0] || "").trim();
	const hasImage = imageSrc.length > 0;

	// SEO: schema Product con ImageObject
	const productSchema = {
		"@context": "https://schema.org",
		"@type": "Product",
		name: product.name,
		image: hasImage
			? {
					"@type": "ImageObject",
					url: imageSrc,
					name: `${product.name} - ${product.brand ?? catLabel} - SpartechRD`,
					caption: `${product.name} disponible en SpartechRD Santo Domingo`,
					contentUrl: imageSrc,
					representativeOfPage: true,
				}
			: undefined,
		brand: product.brand
			? { "@type": "Brand", name: product.brand }
			: undefined,
		description: product.description
			? `${product.description.substring(0, 150)}${product.description.length > 150 ? "..." : ""}`
			: undefined,
		sku: product.code ?? product.sku,
		offers: {
			"@type": "Offer",
			priceCurrency: "DOP",
			price: product.price ?? 0,
			availability: outOfStock
				? "https://schema.org/OutOfStock"
				: "https://schema.org/InStock",
			seller: { "@type": "Organization", name: "SpartechRD" },
			url: `https://spartechrd.com/products/${API_CONFIG.COMPANY_ID}/${product.id}`,
		},
	};

	// SEO: alt text optimizado para imágenes
	const imageAlt = `${product.name} - ${product.brand ?? "SpartechRD"} ${catLabel} disponible en Santo Domingo`;
	const imageTitle = `${product.name} | ${product.brand ?? "Hardware"} | SpartechRD`;

	return (
		<>
			{showAvailability && (
				<AvailabilityModal
					product={product}
					onClose={() => setShowAvailability(false)}
				/>
			)}

			{/* Schema JSON-LD */}
			<script type="application/ld+json">
				{JSON.stringify(productSchema)}
			</script>

			<article
				onClick={handleCardClick}
				className={`group relative bg-white rounded-xl overflow-hidden flex flex-col transition-all duration-300 border ${
					compareModeActive && canCompare
						? "border-stone-400 hover:border-stone-600 cursor-pointer"
						: compareModeActive && !canCompare && !inCompare
							? "border-stone-200 opacity-40 cursor-not-allowed"
							: inCompare
								? "border-stone-500"
								: "border-stone-100 hover:border-stone-300"
				}`}
				itemScope
				itemType="https://schema.org/Product"
			>
				{/* Meta hidden schema */}
				<meta itemProp="name" content={product.name} />
				<meta itemProp="sku" content={product.code ?? product.sku ?? ""} />
				{product.brand && <meta itemProp="brand" content={product.brand} />}
				{product.description && (
					<meta
						itemProp="description"
						content={product.description.substring(0, 160)}
					/>
				)}
				<meta itemProp="image" content={imageSrc || ""} />
				<div itemProp="offers" itemScope itemType="https://schema.org/Offer">
					<meta itemProp="price" content={String(product.price ?? 0)} />
					<meta itemProp="priceCurrency" content="DOP" />
					<meta
						itemProp="availability"
						content={
							outOfStock
								? "https://schema.org/OutOfStock"
								: "https://schema.org/InStock"
						}
					/>
				</div>

				{/* ── Image ── */}
				<div className="relative w-full h-40 sm:h-52 overflow-hidden bg-white flex-shrink-0">
					<Link
						to={`/products/${API_CONFIG.COMPANY_ID}/${product.id}`}
						onClick={(e) => compareModeActive && e.preventDefault()}
						className="block w-full h-full cursor-pointer"
					>
						{hasImage ? (
							<SafeImage
								src={imageSrc}
								alt={imageAlt}
								title={imageTitle}
								className="w-full h-full object-contain p-3 sm:p-4 transition-transform duration-500 group-hover:scale-105"
							/>
						) : (
							<div className="w-full h-full bg-zinc-50 flex flex-col items-center justify-center gap-2 p-4 sm:p-6">
								<img
									src={LOGO_FALLBACK}
									alt={product.name}
									className="max-w-[50%] max-h-[50%] object-contain opacity-50"
									loading="lazy"
									draggable={false}
								/>
								<span className="text-[10px] text-zinc-300 text-center line-clamp-2">
									{product.name}
								</span>
							</div>
						)}
					</Link>

					{/* Category badge */}
					{catLabel && (
						<div className="absolute top-2 left-2 z-10">
							<span className="bg-white/90 backdrop-blur-sm text-stone-600 text-[10px] font-medium px-2 py-0.5 rounded-md border border-stone-100">
								{catLabel}
							</span>
						</div>
					)}

					{/* Offer badge */}
					{product.isOnSale && (
						<div className="absolute top-2 right-2 z-10">
							<span className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-md shadow-sm whitespace-nowrap">
								{product.offerLabel
									? product.offerLabel
									: product.discountPercent
										? `-${product.discountPercent}%`
										: "Oferta"}
							</span>
						</div>
					)}

					{/* Out of stock overlay */}
					{outOfStock && (
						<div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
							<span className="bg-stone-800 text-white text-xs font-medium px-3 py-1 rounded-md">
								{t("product_out_stock")}
							</span>
						</div>
					)}

					{/* Compare mode overlay */}
					{compareModeActive && canCompare && (
						<div className="absolute inset-0 bg-stone-500/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
							<div className="bg-stone-800 text-white text-xs font-medium px-3 py-1.5 rounded-md flex items-center gap-1.5">
								<i className="ri-add-line" /> {t("product_add_compare_tip")}
							</div>
						</div>
					)}

					{inCompare && (
						<div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10">
							<span className="bg-stone-800 text-white text-[10px] font-medium px-2.5 py-0.5 rounded-md flex items-center gap-1">
								<i className="ri-swap-line text-[10px]" />{" "}
								{t("product_in_compare_label")}
							</span>
						</div>
					)}
				</div>

				{/* ── Card body ── */}
				<div className="p-3 sm:p-4 flex flex-col flex-1">
					{/* Brand */}
					{product.brand && (
						<p className="text-[10px] font-medium text-stone-400 uppercase tracking-wide mb-1">
							{product.brand}
						</p>
					)}

					<Link
						to={`/products/${API_CONFIG.COMPANY_ID}/${product.id}`}
						onClick={(e) => compareModeActive && e.preventDefault()}
						className="cursor-pointer"
					>
						<h3 className="text-stone-800 font-medium text-sm leading-snug mb-1 line-clamp-2 group-hover:text-stone-600 transition-colors duration-200">
							{product.name}
						</h3>
					</Link>

					{product.description && (
						<p className="text-stone-400 text-xs leading-relaxed mb-2 line-clamp-1">
							{product.description}
						</p>
					)}

					{/* Price + stock row */}
					<div className="flex items-center justify-between mb-2 mt-auto">
						<div className="flex flex-col">
							{product.price ? (
								<>
									{product.isOnSale && product.listPrice ? (
										<span className="text-stone-400 text-[10px] leading-none mb-0.5 line-through">
											RD${product.listPrice.toLocaleString("es-DO")}
										</span>
									) : (
										<span className="text-stone-400 text-[10px] leading-none mb-0.5">
											Desde
										</span>
									)}
									<span
										className={`font-semibold text-sm sm:text-base leading-tight ${
											product.isOnSale ? "text-red-600" : "text-stone-900"
										}`}
									>
										RD${product.price.toLocaleString("es-DO")}
									</span>
								</>
							) : (
								<span className="text-stone-400 text-xs italic">
									{t("product_on_request")}
								</span>
							)}
						</div>
						<span
							className={`text-[10px] px-2 py-0.5 rounded-md font-medium ${
								stockBadge.color === "emerald"
									? "bg-emerald-50 text-emerald-700"
									: "bg-red-50 text-red-600"
							}`}
						>
							{stockBadge.text}
						</span>
					</div>

					{/* Primary CTA */}
					<button
						onClick={(e) => {
							e.stopPropagation();
							if (!outOfStock) setShowAvailability(true);
						}}
						disabled={outOfStock}
						className={`w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-lg transition-colors duration-200 whitespace-nowrap mb-1.5 cursor-pointer ${
							outOfStock
								? "bg-stone-100 text-stone-400 cursor-not-allowed"
								: "bg-[#111111] text-[#ffffff] hover:bg-[#000000]"
						}`}
					>
						<i className="ri-map-pin-2-line text-xs" />
						{outOfStock ? "Sin stock" : "Ver disponibilidad"}
					</button>

					{/* Secondary: detail + compare */}
					<div className="flex gap-2">
						<Link
							to={`/products/${API_CONFIG.COMPANY_ID}/${product.id}`}
							onClick={(e) => compareModeActive && e.preventDefault()}
							className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] font-medium rounded-md border border-stone-200 text-stone-500 hover:border-stone-300 hover:text-stone-700 transition-colors cursor-pointer whitespace-nowrap"
						>
							<i className="ri-eye-line" />
							<span className="hidden sm:inline">
								{t("product_view_detail")}
							</span>
							<span className="sm:hidden">Ver</span>
						</Link>
						<button
							onClick={(e) => {
								e.stopPropagation();
								if (inCompare) removeFromCompare(product.id);
								else if (canCompare) addToCompare(product);
							}}
							className={`flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-medium rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
								inCompare
									? "border-stone-400 bg-stone-50 text-stone-700"
									: canCompare
										? "border-stone-200 text-stone-400 hover:border-stone-300 hover:text-stone-600"
										: "border-stone-100 text-stone-200 cursor-not-allowed"
							}`}
						>
							<i className={inCompare ? "ri-check-line" : "ri-swap-line"} />
						</button>
					</div>
				</div>
			</article>
		</>
	);
}
